const UNIT_PRICE_MIN_PARAM = 'filter.unit_price.gte';
const UNIT_PRICE_MAX_PARAM = 'filter.unit_price.lte';
const PRICE_FILTER_KEYS = ['filter.v.price.gte', 'filter.v.price.lte', 'filter.p.price.gte', 'filter.p.price.lte'];
const UNIT_PRICE_AGGREGATION_CACHE_LIMIT = 8;
const unitPriceAggregationCache = new Map();

const normalizePriceValue = (value) => {
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (trimmed === '') return null;

  const onlyNumbersAndSeparators = trimmed.replace(/[^\d.,-]/g, '');
  if (onlyNumbersAndSeparators === '') return null;

  let normalized = onlyNumbersAndSeparators;

  if (normalized.includes(',') && normalized.includes('.')) {
    normalized = normalized.replace(/,/g, '');
  } else if (normalized.includes(',')) {
    normalized = normalized.replace(/,/g, '.');
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const getPriceCandidatesFromText = (text) => {
  if (typeof text !== 'string' || text.trim() === '') return [];

  const currencyMatches = Array.from(
    text.matchAll(/(?:[$€£]\s*[-+]?\d[\d,]*(?:\.\d+)?|[-+]?\d[\d,]*(?:\.\d+)?\s*[$€£])/g)
  );

  if (currencyMatches.length > 0) {
    return currencyMatches
      .map((match) => ({ value: normalizePriceValue(match[0]), index: match.index ?? 0 }))
      .filter((entry) => entry.value !== null && entry.value >= 0);
  }

  // Fallback: only consider decimal numbers so coverage/count integers (e.g. 16 sq ft) don't become prices.
  return Array.from(text.matchAll(/[-+]?\d[\d,]*\.\d+/g))
    .map((match) => ({ value: normalizePriceValue(match[0]), index: match.index ?? 0 }))
    .filter((entry) => entry.value !== null && entry.value >= 0);
};

const scoreUnitPriceCandidate = (text, candidate) => {
  const start = Math.max(0, candidate.index || 0);
  const before = text.slice(Math.max(0, start - 16), start).toLowerCase();
  const after = text.slice(start, Math.min(text.length, start + 56)).toLowerCase();
  const context = `${before} ${after}`;

  let score = 0;

  if (/(\/|per)\s*(sq|sf|ft|m2|ea|each|pc|piece|tile|unit|uom|linear|lf)/.test(context)) {
    score += 3;
  }

  if (/(\/|per)\s*(box|case|carton|pallet|pack|bundle)/.test(context)) {
    score -= 4;
  }

  return score;
};

const parseUnitPriceFromText = (text) => {
  const candidates = getPriceCandidatesFromText(text);
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0].value;

  let best = candidates[0];
  let bestScore = scoreUnitPriceCandidate(text, best);

  for (let index = 1; index < candidates.length; index += 1) {
    const current = candidates[index];
    const currentScore = scoreUnitPriceCandidate(text, current);

    if (currentScore > bestScore) {
      best = current;
      bestScore = currentScore;
      continue;
    }

    // Stable tie-breaker: prefer earlier value in the display string.
    if (currentScore === bestScore && current.index < best.index) {
      best = current;
      bestScore = currentScore;
    }
  }

  return best.value;
};

const getCardUnitPriceValue = (card) => {
  if (!card) return null;

  const numericValue = normalizePriceValue(card.dataset.unitPriceValue);
  if (numericValue !== null) return numericValue;

  return parseUnitPriceFromText(card.dataset.unitPriceText);
};

const syncUnitPriceMaxToControls = (search, maxValue) => {
  if (maxValue === null) return;

  const { max: activeMaxFromSearch } = getUnitPriceBoundsFromSearch(search);

  document.querySelectorAll('price-range').forEach((el) => {
    const numberInputs = el.querySelectorAll('.facets__price-wrapper .field__input');
    const rangeInputs = el.querySelectorAll('.facets__range .field__range');
    if (numberInputs.length < 2 || rangeInputs.length < 2) return;

    const minNumberInput = numberInputs[0];
    const maxNumberInput = numberInputs[1];
    const minRangeInput = rangeInputs[0];
    const maxRangeInput = rangeInputs[1];

    minNumberInput.setAttribute('max', maxValue);
    maxNumberInput.setAttribute('max', maxValue);
    minRangeInput.setAttribute('max', maxValue);
    maxRangeInput.setAttribute('max', maxValue);

    const currentMin = normalizePriceValue(minNumberInput.value) || 0;
    const currentMax = normalizePriceValue(maxNumberInput.value);
    const boundedMax = currentMax === null ? maxValue : Math.min(currentMax, maxValue);
    const boundedMin = Math.min(currentMin, boundedMax);

    minNumberInput.value = boundedMin;
    maxNumberInput.value = boundedMax;
    minRangeInput.value = boundedMin;
    maxRangeInput.value = boundedMax;

    if (activeMaxFromSearch === null) {
      maxNumberInput.value = maxValue;
      maxRangeInput.value = maxValue;
    }

    if (typeof el.setMinAndMaxValues === 'function') el.setMinAndMaxValues();
    if (typeof el.fillSlider === 'function') el.fillSlider();
    if (typeof el.updateLabel === 'function') el.updateLabel();
  });
};

const getUnitPriceMaxFromGrid = () => {
  const productGridContainer = document.getElementById('ProductGridContainer');
  const cachedMax = normalizePriceValue(productGridContainer?.dataset.unitPriceMax);
  if (cachedMax !== null) return cachedMax;

  const prices = Array.from(document.querySelectorAll('.card-wrapper[data-unit-price-value], .card-wrapper[data-unit-price-text]'))
    .map((card) => getCardUnitPriceValue(card))
    .filter((price) => price !== null);

  return prices.length > 0 ? Math.max(...prices) : null;
};

const getFacetPaginationState = () => {
  const productGridContainer = document.getElementById('ProductGridContainer');
  const productGrid = document.getElementById('product-grid');

  return {
    productGridContainer,
    productGrid,
    sectionId: productGrid?.dataset.id || productGridContainer?.dataset.id || null,
    productsPerPage: Number.parseInt(productGridContainer?.dataset.productsPerPage || '', 10) || null,
    totalPages: Number.parseInt(productGridContainer?.dataset.totalPages || '', 10) || 1,
  };
};

const getGridItemsFromHtml = (html) => {
  const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
  const productGridContainer = parsedHTML.getElementById('ProductGridContainer');

  if (!productGridContainer) return [];

  return Array.from(productGridContainer.querySelectorAll('#product-grid > .grid__item'));
};

const getMaxUnitPriceFromItems = (items) => {
  let maxUnitPriceSeen = null;

  items.forEach((item) => {
    const unitPrice = getGridItemUnitPrice(item);
    if (unitPrice === null) return;
    maxUnitPriceSeen = maxUnitPriceSeen === null ? unitPrice : Math.max(maxUnitPriceSeen, unitPrice);
  });

  return maxUnitPriceSeen;
};

const getServerStateSearchParams = (search) => {
  const params = new URLSearchParams(search || window.location.search);
  params.delete('page');
  params.delete(UNIT_PRICE_MIN_PARAM);
  params.delete(UNIT_PRICE_MAX_PARAM);
  PRICE_FILTER_KEYS.forEach((key) => params.delete(key));
  params.sort();
  return params;
};

const getServerStateSearchKey = (search) => getServerStateSearchParams(search).toString();

const setUnitPriceAggregationCache = (cacheKey, value) => {
  if (unitPriceAggregationCache.has(cacheKey)) {
    unitPriceAggregationCache.delete(cacheKey);
  }

  unitPriceAggregationCache.set(cacheKey, value);

  while (unitPriceAggregationCache.size > UNIT_PRICE_AGGREGATION_CACHE_LIMIT) {
    const oldestKey = unitPriceAggregationCache.keys().next().value;
    unitPriceAggregationCache.delete(oldestKey);
  }
};

const getUnitPriceAggregationCacheKey = (search, sectionId, totalPages) => {
  const serverStateKey = getServerStateSearchKey(search);
  return `${window.location.pathname}|${sectionId}|${totalPages}|${serverStateKey}`;
};

const getAggregatedUnitPriceItems = async (search) => {
  const paginationState = getFacetPaginationState();
  if (!paginationState.sectionId || paginationState.totalPages <= 1) return null;

  const serverStateSearchParams = getServerStateSearchParams(search);
  const cacheKey = getUnitPriceAggregationCacheKey(search, paginationState.sectionId, paginationState.totalPages);
  const cachedAggregation = unitPriceAggregationCache.get(cacheKey);
  if (cachedAggregation) return cachedAggregation;

  const fetchPromises = Array.from({ length: paginationState.totalPages }, (_, index) => {
    const page = index + 1;
    return fetchGridPageItems(page, serverStateSearchParams, paginationState.sectionId);
  });

  const allPageItems = await Promise.all(fetchPromises);
  const aggregatedItems = allPageItems
    .flat()
    .map((item) => ({
      html: item.outerHTML,
      unitPrice: getCardUnitPrice(item),
    }));

  setUnitPriceAggregationCache(cacheKey, aggregatedItems);
  return aggregatedItems;
};

const fetchGridPageItems = async (page, searchParams, sectionId) => {
  const pageParams = new URLSearchParams(searchParams);

  if (page > 1) {
    pageParams.set('page', String(page));
  } else {
    pageParams.delete('page');
  }

  const url = `${window.location.pathname}?section_id=${sectionId}&${pageParams.toString()}`;
  const response = await fetch(url);
  const html = await response.text();

  return getGridItemsFromHtml(html);
};

const setProductCountDisplay = (count) => {
  [document.getElementById('ProductCount'), document.getElementById('ProductCountDesktop')].forEach((el) => {
    if (!el) return;
    const text = el.textContent.trim();
    const updated = text.replace(/^\d+/, count);
    if (updated !== text) el.textContent = updated;
  });
};

const setPaginationVisibility = (isVisible) => {
  document.querySelectorAll('.pagination-wrapper').forEach((wrapper) => {
    wrapper.style.display = isVisible ? '' : 'none';
  });
};

const getCurrentPageFromSearch = (search) => {
  const params = new URLSearchParams(search || window.location.search);
  const currentPage = Number.parseInt(params.get('page') || '1', 10);
  return Number.isFinite(currentPage) && currentPage > 0 ? currentPage : 1;
};

const buildPageSearchParams = (search, page) => {
  const params = new URLSearchParams(search || window.location.search);
  if (page > 1) {
    params.set('page', String(page));
  } else {
    params.delete('page');
  }
  return params.toString();
};

const renderClientPagination = (totalPages, currentPage, search) => {
  const wrapper = document.querySelector('.pagination-wrapper');
  if (!wrapper) return;

  if (totalPages <= 1) {
    wrapper.style.display = 'none';
    wrapper.innerHTML = '';
    return;
  }

  wrapper.style.display = '';

  const pageLinks = [];
  const pageSearch = new URLSearchParams(search || window.location.search);
  pageSearch.delete('page');

  const buildLink = (page) => {
    const linkParams = new URLSearchParams(pageSearch);
    if (page > 1) linkParams.set('page', String(page));
    return `${window.location.pathname}?${linkParams.toString()}`;
  };

  pageLinks.push(`<li><a href="${buildLink(Math.max(1, currentPage - 1))}" class="pagination__item pagination__item--next pagination__item-arrow motion-reduce ${currentPage > 1 ? 'active' : 'disabled'}" aria-label="${window.theme?.strings?.paginationPrevious || 'Previous'}">Prev</a></li>`);

  for (let page = 1; page <= totalPages; page += 1) {
    if (page === currentPage) {
      pageLinks.push(`<li><a role="link" aria-disabled="true" class="pagination__item pagination__item--current num" aria-current="page" aria-label="Page ${page}">${page}</a></li>`);
    } else {
      pageLinks.push(`<li><a href="${buildLink(page)}" class="pagination__item num" aria-label="Page ${page}">${page}</a></li>`);
    }
  }

  pageLinks.push(`<li><a href="${buildLink(Math.min(totalPages, currentPage + 1))}" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce ${currentPage < totalPages ? 'active' : 'disabled'}" aria-label="${window.theme?.strings?.paginationNext || 'Next'}">Next</a></li>`);

  wrapper.innerHTML = `
    <nav class="pagination" role="navigation" aria-label="Pagination">
      <ul class="pagination__list list-unstyled" role="list">
        ${pageLinks.join('')}
      </ul>
    </nav>
  `;

  wrapper.querySelectorAll('.pagination__item[href]').forEach((link) => {
    if (link.dataset.facetPaginationBound) return;
    link.dataset.facetPaginationBound = '1';

    link.addEventListener('click', (event) => {
      event.preventDefault();
      const linkUrl = new URL(link.href, window.location.origin);
      FacetFiltersForm.renderPage(linkUrl.searchParams.toString());
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  });
};

const getUnitPriceBoundsFromSearch = (search) => {
  const params = new URLSearchParams(search || window.location.search);

  // Prefer custom unit-price params; fall back to standard Shopify price filter params
  // so that pagination links (full-page reloads with filter.v.price.* / filter.p.price.*) are respected.
  const minFallbackKeys = ['filter.v.price.gte', 'filter.p.price.gte'];
  const maxFallbackKeys = ['filter.v.price.lte', 'filter.p.price.lte'];

  let min = normalizePriceValue(params.get(UNIT_PRICE_MIN_PARAM));
  if (min === null) {
    for (const key of minFallbackKeys) {
      min = normalizePriceValue(params.get(key));
      if (min !== null) break;
    }
  }

  let max = normalizePriceValue(params.get(UNIT_PRICE_MAX_PARAM));
  if (max === null) {
    for (const key of maxFallbackKeys) {
      max = normalizePriceValue(params.get(key));
      if (max !== null) break;
    }
  }

  return { min: min || 0, max };
};

const getSortByFromSearch = (search) => {
  const params = new URLSearchParams(search || window.location.search);
  const fromSearch = (params.get('sort_by') || '').trim();
  if (fromSearch) return fromSearch;

  const checkedSortInput = document.querySelector('input[name="sort_by"]:checked');
  if (checkedSortInput?.value) return checkedSortInput.value;

  const selectedSortInput = document.querySelector('.sort-by--item.selected input[name="sort_by"]');
  return selectedSortInput?.value || '';
};

const getUnitPriceSortDirection = (sortBy) => {
  if (sortBy === 'price-ascending') return 'asc';
  if (sortBy === 'price-descending') return 'desc';
  return null;
};

const hasUnitPriceDrivenState = (search) => {
  const { min, max } = getUnitPriceBoundsFromSearch(search);
  if (min > 0 || max !== null) return true;

  const sortBy = getSortByFromSearch(search);
  return getUnitPriceSortDirection(sortBy) !== null;
};

const shouldHydrateFacetsOnLoad = (search) => {
  return hasUnitPriceDrivenState(search);
};

const getCardUnitPrice = (item) => {
  const card = item.querySelector('.card-wrapper[data-unit-price-value], .card-wrapper[data-unit-price-text]');
  if (!card) return null;
  return getCardUnitPriceValue(card);
};

const getGridItemUnitPrice = (item) => {
  if (!item) return null;
  if (Object.prototype.hasOwnProperty.call(item, 'unitPrice')) return item.unitPrice;
  return getCardUnitPrice(item);
};

const sortItemsByUnitPrice = (items, direction) => {
  if (!direction) return;

  items.sort((a, b) => {
    const aPrice = getGridItemUnitPrice(a);
    const bPrice = getGridItemUnitPrice(b);

    // Keep cards with unknown unit prices at the end.
    if (aPrice === null && bPrice === null) return 0;
    if (aPrice === null) return 1;
    if (bPrice === null) return -1;

    return direction === 'asc' ? aPrice - bPrice : bPrice - aPrice;
  });
};

const applyUnitPriceFilterToGrid = async (search) => {
  const productGrid = document.getElementById('product-grid');
  if (!productGrid) return;

  const paginationState = getFacetPaginationState();
  const { min, max } = getUnitPriceBoundsFromSearch(search);
  const hasActiveFilter = min > 0 || max !== null;
  const sortBy = getSortByFromSearch(search);
  const unitPriceSortDirection = getUnitPriceSortDirection(sortBy);
  const shouldUseClientAggregation = hasActiveFilter || unitPriceSortDirection !== null;
  const gridItems = productGrid.querySelectorAll('.grid__item');

  if (!shouldUseClientAggregation) {
    gridItems.forEach((item) => {
      item.style.removeProperty('display');
    });
    setPaginationVisibility(true);

    const aggregatedItems = await getAggregatedUnitPriceItems(search);
    const currentGridMax = aggregatedItems ? getMaxUnitPriceFromItems(aggregatedItems) : getUnitPriceMaxFromGrid();
    if (paginationState.productGridContainer && currentGridMax !== null) {
      paginationState.productGridContainer.dataset.unitPriceMax = String(currentGridMax);
    }
    syncUnitPriceMaxToControls(search, currentGridMax);
    return;
  }

  const currentPage = getCurrentPageFromSearch(search);

  const matchingItems = [];
  let maxUnitPriceSeen = null;

  if (!paginationState.sectionId) {
    Array.from(gridItems).forEach((item) => {
      const unitPrice = getCardUnitPrice(item);

      if (!hasActiveFilter || unitPrice === null || (unitPrice >= min && (max === null || unitPrice <= max))) {
        matchingItems.push(item);
      }

      if (unitPrice !== null) {
        maxUnitPriceSeen = maxUnitPriceSeen === null ? unitPrice : Math.max(maxUnitPriceSeen, unitPrice);
      }
    });

    sortItemsByUnitPrice(matchingItems, unitPriceSortDirection);
    productGrid.innerHTML = matchingItems.map((item) => item.outerHTML).join('');
    setProductCountDisplay(matchingItems.length);
    setPaginationVisibility(true);
    syncUnitPriceMaxToControls(search, maxUnitPriceSeen);
    return;
  }

  const aggregatedItems = await getAggregatedUnitPriceItems(search)
    || Array.from(gridItems).map((item) => ({
      html: item.outerHTML,
      unitPrice: getCardUnitPrice(item),
    }));

  aggregatedItems.forEach((item) => {
    const unitPrice = item.unitPrice;
    if (unitPrice !== null) {
      maxUnitPriceSeen = maxUnitPriceSeen === null ? unitPrice : Math.max(maxUnitPriceSeen, unitPrice);
    }

    if (!hasActiveFilter || unitPrice === null || (unitPrice >= min && (max === null || unitPrice <= max))) {
      matchingItems.push(item);
    }
  });

  sortItemsByUnitPrice(matchingItems, unitPriceSortDirection);

  if (paginationState.productGridContainer && maxUnitPriceSeen !== null) {
    paginationState.productGridContainer.dataset.unitPriceMax = String(maxUnitPriceSeen);
  }

  syncUnitPriceMaxToControls(search, maxUnitPriceSeen);

  const pageSize = gridItems.length || paginationState.productsPerPage || matchingItems.length || 1;
  const filteredTotalPages = Math.max(1, Math.ceil(matchingItems.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, filteredTotalPages);
  const pageStart = (safeCurrentPage - 1) * pageSize;
  const pageItems = matchingItems.slice(pageStart, pageStart + pageSize);

  productGrid.innerHTML = pageItems.map((item) => item.html).join('');
  setProductCountDisplay(matchingItems.length);
  renderClientPagination(filteredTotalPages, safeCurrentPage, search);

  document.querySelectorAll('price-range').forEach((el) => {
    if (typeof el.syncFromSearchParams === 'function') el.syncFromSearchParams();
    if (typeof el.updateLabel === 'function') el.updateLabel();
  });
};

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', (event) => {
      // Avoid re-rendering while the range thumb is still being dragged.
      if (event.target?.type === 'range') return;
      this.debouncedOnSubmit(event);
    });
    facetForm.addEventListener('change', (event) => {
      if (event.target?.name === 'sort_by') {
        this.onSubmitHandler(event);
        return;
      }

      if (event.target?.type === 'range') {
        // Safari can emit range change events while the thumb is still active.
        // Only submit once the drag interaction has completed.
        if (event.target.matches(':active')) return;
        this.onSubmitHandler(event);
        return;
      }

      this.debouncedOnSubmit(event);
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    this.initNavigation();
  }

  initNavigation() {
    const navigationLayouts = document.querySelectorAll('.navigation-layout');
    let productGrid = document.getElementById('product-grid');

    if (navigationLayouts.length === 0) return;
  
    navigationLayouts.forEach((navigationLayout) => {
      const navigationItems = navigationLayout.querySelectorAll('span');
      productGrid = document.getElementById('product-grid');
  
      const saveStateToSessionStorage = () => {
        sessionStorage.setItem('productGridView', productGrid.classList.contains('grid-list'));
    
        let activeIndex = -1;
        navigationItems.forEach((item, index) => {
          if (item.classList.contains('active')) {
            activeIndex = index;
          }
        });
    
        sessionStorage.setItem('activeIndex', activeIndex);
      };
    
      const restoreStateFromSessionStorage = () => {
        const isGridList = sessionStorage.getItem('productGridView') === 'true';
        if (isGridList) {
          productGrid.classList.add('grid-list');
        }
    
        const activeIndex = sessionStorage.getItem('activeIndex');
        if (activeIndex !== null) {
          navigationItems.forEach((item, index) => {
            if (index === parseInt(activeIndex)) {
              item.classList.add('active');
            } else {
              item.classList.remove('active');
            }
          });
        }
      };
    
      navigationItems.forEach((item) => {
        item.addEventListener('click', () => {
          navigationItems.forEach((navItem) => {
            navItem.classList.remove('active');
          });
    
          item.classList.add('active');
    
          if (item.classList.contains('list-view')) {
            productGrid.classList.add('grid-list');
            saveStateToSessionStorage();
          } else if (item.classList.contains('thumbnail-view')) {
            productGrid.classList.remove('grid-list');
            saveStateToSessionStorage();
          }
        });
      });
    
      restoreStateFromSessionStorage();
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    const previousSearchParams = FacetFiltersForm.searchParamsPrev;
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sameServerState = getServerStateSearchKey(previousSearchParams || '') === getServerStateSearchKey(searchParams);
    const canUseClientOnlyRender = sameServerState && hasUnitPriceDrivenState(searchParams);

    if (canUseClientOnlyRender) {
      const countContainer = document.getElementById('ProductCount');
      const countContainerDesktop = document.getElementById('ProductCountDesktop');
      const loadingSpinners = document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner');
      const collectionContainer = document.getElementById('ProductGridContainer')?.querySelector('.collection');

      loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
      if (countContainer) countContainer.classList.add('loading');
      if (countContainerDesktop) countContainerDesktop.classList.add('loading');
      if (collectionContainer) collectionContainer.classList.add('loading', 'loading-images');

      (async () => {
        try {
          await applyUnitPriceFilterToGrid(searchParams);
          if (collectionContainer) collectionContainer.classList.remove('loading', 'loading-images');
          FacetFiltersForm.bindPaginationLinks();
        } finally {
          if (countContainer) countContainer.classList.remove('loading');
          if (countContainerDesktop) countContainerDesktop.classList.remove('loading');
          loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
        }
      })();

      if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
      return;
    }

    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner');
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');

    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, searchParams)
        : FacetFiltersForm.renderSectionFromFetch(url, event, searchParams);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event, searchParams) {
    fetch(url)
      .then((response) => response.text())
      .then(async (responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductCount(html);
        await FacetFiltersForm.renderProductGridContainer(html, searchParams);

        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(document);

        document.querySelectorAll('price-range').forEach((el) => el.updateLabel && el.updateLabel());
        document.querySelector('facet-filters-form').initNavigation();
      });
  }

  static async renderSectionFromCache(filterDataUrl, event, searchParams) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductCount(html);
    await FacetFiltersForm.renderProductGridContainer(html, searchParams);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(document);

    document.querySelectorAll('price-range').forEach((el) => el.updateLabel && el.updateLabel());
    document.querySelector('facet-filters-form').initNavigation();
  }

  static async renderProductGridContainer(html, searchParams) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    const collectionContainer = document
      .getElementById('ProductGridContainer')
      .querySelector('.collection');

    if (collectionContainer) {
      collectionContainer.classList.add('loading', 'loading-images');
    }

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    document.querySelector('facet-filters-form').initNavigation();

    await applyUnitPriceFilterToGrid(searchParams);

    if (typeof window.initializeCollectionGridReveal === 'function') {
      window.initializeCollectionGridReveal(document.getElementById('ProductGridContainer'), { force: true });
    } else if (collectionContainer) {
      collectionContainer.classList.remove('loading', 'loading-images');
    }

    FacetFiltersForm.bindPaginationLinks();
  }

  static bindPaginationLinks() {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;

    container.querySelectorAll('.pagination__item[href]').forEach((link) => {
      // Avoid attaching duplicate listeners by marking the element
      if (link.dataset.facetPaginationBound) return;
      link.dataset.facetPaginationBound = '1';

      link.addEventListener('click', (event) => {
        event.preventDefault();

        const linkUrl = new URL(link.href);
        const linkParams = new URLSearchParams(linkUrl.search);

        // Merge current filter state (from browser URL) with the page param from the clicked link
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.delete('page');

        const pageValue = linkParams.get('page');
        if (pageValue) {
          currentParams.set('page', pageValue);
        }

        FacetFiltersForm.renderPage(currentParams.toString());
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner');
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };
    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }
        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);
        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header, .facets__header-vertical');
    const sourceHeaderElement = source.querySelector('.facets__header, .facets__header-vertical');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const searchParams = new URLSearchParams();

    let unitPriceMin = null;
    let unitPriceMax = null;

    const maxInput = form.querySelector(
      'input[name="filter.v.price.lte"], input[name="filter.p.price.lte"]'
    );
    // Use the current control max as the source of truth for "no upper bound".
    // Grid-derived max can be lower on paginated/filtered views and causes thumb jumps.
    const defaultUnitPriceMax = (maxInput ? normalizePriceValue(maxInput.getAttribute('max')) : null)
      ?? getUnitPriceMaxFromGrid();

    formData.forEach((value, key) => {
      const stringValue = typeof value === 'string' ? value.trim() : value;

      if (PRICE_FILTER_KEYS.includes(key)) {
        if (key.endsWith('.gte')) unitPriceMin = normalizePriceValue(String(stringValue));
        if (key.endsWith('.lte')) unitPriceMax = normalizePriceValue(String(stringValue));
        return;
      }

      if (stringValue === '') return;
      searchParams.append(key, stringValue);
    });

    if (unitPriceMin !== null && unitPriceMin > 0) {
      searchParams.set(UNIT_PRICE_MIN_PARAM, unitPriceMin.toString());
    }

    if (
      unitPriceMax !== null
      && unitPriceMax > 0
      && (defaultUnitPriceMax === null || unitPriceMax < defaultUnitPriceMax)
    ) {
      searchParams.set(UNIT_PRICE_MAX_PARAM, unitPriceMax.toString());
    }

    return searchParams.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';
      const facetDrawer = document.querySelector('.facets-drawer');

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          } else if (facetDrawer && window.innerWidth > 750 && form.id === 'FacetFiltersFormMobile') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });

      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1 ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
    this.initNavigation();
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.bindPaginationLinks();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.syncFromSearchParams();
    this.setMinAndMaxValues();
    this.controlSlider();
    this.controlInput();
    this.fillSlider();
    this.bindLabelUpdate();
  }

  bindLabelUpdate() {
    // The max number input is the second .field__input inside this element
    const maxNumberInput = this.querySelectorAll('.facets__price-wrapper .field__input')[1];
    // The max range input is the second .field__range inside this element
    const maxRangeInput = this.querySelectorAll('.facets__range .field__range')[1];
    if (!maxNumberInput) return;

    this._updateLabel = () => {
      const jsFilter = this.closest('.js-filter');
      if (!jsFilter) return;
      // Desktop: .facets__selected, Mobile: .mobile-facets__info
      const label = jsFilter.querySelector('.facets__selected, .mobile-facets__info');
      if (!label) return;

      const currencySymbol = this.querySelector('.field-currency')?.textContent?.trim() || '';
      const rawValue = maxRangeInput?.value || maxNumberInput.value || maxNumberInput.getAttribute('max');
      const num = Number.parseFloat(rawValue);
      if (!Number.isFinite(num)) return;

      // Build price string matching Shopify's money format (e.g. $354.29)
      const priceStr = currencySymbol + num.toFixed(2);

      // Replace the price portion: everything after the last space (or the full text if no space)
      const text = label.textContent.trim();
      const lastSpace = text.lastIndexOf(' ');
      label.textContent = lastSpace >= 0
        ? text.substring(0, lastSpace + 1) + priceStr
        : priceStr;
    };

    maxNumberInput.addEventListener('input', this._updateLabel);
    if (maxRangeInput) maxRangeInput.addEventListener('input', this._updateLabel);

    // Run immediately so the label reflects the current slider value on load / after AJAX re-render
    this._updateLabel();
  }

  updateLabel() {
    if (typeof this._updateLabel === 'function') this._updateLabel();
  }

  syncFromSearchParams() {
    const numberInputs = this.querySelectorAll('.facets__price-wrapper .field__input');
    const rangeInputs = this.querySelectorAll('.facets__range .field__range');

    if (numberInputs.length < 2 || rangeInputs.length < 2) return;

    const gridMax = getUnitPriceMaxFromGrid();

    const minInput = numberInputs[0];
    const maxInput = numberInputs[1];
    const rangeMinInput = rangeInputs[0];
    const rangeMaxInput = rangeInputs[1];

    if (gridMax !== null) {
      minInput.setAttribute('max', gridMax);
      maxInput.setAttribute('max', gridMax);
      rangeMinInput.setAttribute('max', gridMax);
      rangeMaxInput.setAttribute('max', gridMax);
    }

    const { min, max } = getUnitPriceBoundsFromSearch(window.location.search);

    const maxAttrValue = normalizePriceValue(maxInput.getAttribute('max'));
    const boundedMaxFromSearch =
      max !== null && maxAttrValue !== null ? Math.min(max, maxAttrValue) : max;
    const appliedMax = boundedMaxFromSearch === null ? maxAttrValue : boundedMaxFromSearch;
    const appliedMin = Math.max(min || 0, 0);

    const finalMax = Number.isFinite(appliedMax) ? appliedMax : null;
    const finalMin = finalMax !== null ? Math.min(appliedMin, finalMax) : appliedMin;

    if (finalMin !== null) {
      minInput.value = finalMin;
      rangeMinInput.value = finalMin;
    }

    if (finalMax !== null) {
      maxInput.value = finalMax;
      rangeMaxInput.value = finalMax;
    }

    rangeMinInput.style.zIndex = 2;
    rangeMaxInput.style.zIndex = 1;

    this.fillSlider();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const numberInputs = this.querySelectorAll('.facets__price-wrapper .field__input');
    const minInput = numberInputs[0];
    const maxInput = numberInputs[1];

    if (!minInput || !maxInput) return;

    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }

  fillSlider() {
    this.querySelectorAll('.facets__range').forEach((inputWrapper) => {
      const inputsRange = inputWrapper.querySelectorAll(".field__range");
      const inputRangeFrom = inputsRange[0];
      const inputRangeTo = inputsRange[1];
      if (!inputRangeFrom || !inputRangeTo) return;

      const range = inputRangeTo.max - inputRangeTo.min;
      if (!Number.isFinite(range) || range <= 0) return;

      const fromCurrent = inputRangeFrom.value - inputRangeTo.min;
      const toCurrent = inputRangeTo.value - inputRangeTo.min;
      const minRange = (fromCurrent / range) * 100;
      const maxRange = (toCurrent / range) * 100;

      inputWrapper.setAttribute("style", `--range-min: ${minRange}%; --range-max: ${maxRange}%`);
    });
  }

  controlSlider() {
    const inputRangeWrappers = this.querySelectorAll('.facets__range');
    const inputNumberWrappers = this.querySelectorAll('.facets__price-wrapper');

    inputRangeWrappers.forEach((inputWrapper, index) => {
      const inputsRange = inputWrapper.querySelectorAll(".field__range");
      const inputRangeFrom = inputsRange[0];
      const inputRangeTo = inputsRange[1];
      const numberInputs = inputNumberWrappers[index]?.querySelectorAll('.field__input');
      const inputNumberFrom = numberInputs?.[0];
      const inputNumberTo = numberInputs?.[1];
      if (!inputRangeFrom || !inputRangeTo || !inputNumberFrom || !inputNumberTo) return;

      inputRangeFrom.oninput = () => {
        const from = Number.parseFloat(inputRangeFrom.value);
        const to = Number.parseFloat(inputRangeTo.value);
        if (!Number.isFinite(from) || !Number.isFinite(to)) return;

        if (from > to) {
          inputRangeFrom.value = to;
          inputNumberFrom.value = to;
        } else {
          inputNumberFrom.value = from;
        }

        inputRangeFrom.style.zIndex = 2;
        inputRangeTo.style.zIndex = 1;
        this.fillSlider();
      };

      inputRangeFrom.style.zIndex = 2;
      inputRangeTo.style.zIndex = 1;

      inputRangeTo.oninput = () => {
        const from = Number.parseFloat(inputRangeFrom.value);
        const to = Number.parseFloat(inputRangeTo.value);
        if (!Number.isFinite(from) || !Number.isFinite(to)) return;

        if (from <= to) {
          inputRangeTo.value = to;
          inputNumberTo.value = to;
        } else {
          inputNumberTo.value = from;
          inputRangeTo.value = from;
        }

        inputRangeTo.style.zIndex = 2;
        inputRangeFrom.style.zIndex = 1;
        this.fillSlider();
      };
    });
  }

  controlInput() {
    const inputRangeWrappers = this.querySelectorAll('.facets__range');

    this.querySelectorAll('.facets__price-wrapper').forEach((inputWrapper, index) => {
      const inputsNumber = inputWrapper.querySelectorAll(".field__input");
      const inputNumberFrom = inputsNumber[0];
      const inputNumberTo = inputsNumber[1];
      const rangeInputs = inputRangeWrappers[index]?.querySelectorAll('.field__range');
      const inputRangeFrom = rangeInputs?.[0];
      const inputRangeTo = rangeInputs?.[1];
      if (!inputNumberFrom || !inputNumberTo || !inputRangeFrom || !inputRangeTo) return;

      inputNumberFrom.oninput = () => {
        const from = Number.parseFloat(inputNumberFrom.value);
        const to = Number.parseFloat(inputNumberTo.value);
        if (!Number.isFinite(from) || !Number.isFinite(to)) return;

        if (from > to) {
          inputRangeFrom.value = to;
          inputNumberFrom.value = to;
        } else {
          inputRangeFrom.value = from;
          inputNumberFrom.value = from;
        }

        inputRangeFrom.style.zIndex = 2;
        inputRangeTo.style.zIndex = 1;
        this.fillSlider();
      };

      inputNumberTo.oninput = () => {
        const from = Number.parseFloat(inputNumberFrom.value);
        const to = Number.parseFloat(inputNumberTo.value);
        if (!Number.isFinite(from) || !Number.isFinite(to)) return;

        if (from <= to) {
          inputRangeTo.value = to;
          inputNumberTo.value = to;
        } else {
          inputRangeTo.value = from;
          inputNumberTo.value = from;
        }

        inputRangeTo.style.zIndex = 2;
        inputRangeFrom.style.zIndex = 1;
        this.fillSlider();
      };
    });
  }

}

customElements.define('price-range', PriceRange);

const initializeUnitPriceFilterOnLoad = async () => {
  const initialSearch = window.location.search.slice(1);
  const collectionContainer = document
    .getElementById('ProductGridContainer')
    ?.querySelector('.collection');

  if (collectionContainer) {
    collectionContainer.classList.add('loading', 'loading-images');
  }

  if (shouldHydrateFacetsOnLoad(initialSearch)) {
    FacetFiltersForm.renderPage(initialSearch, null, false);
    return;
  }

  try {
    await applyUnitPriceFilterToGrid(window.location.search);
  } finally {
    if (collectionContainer) {
      collectionContainer.classList.remove('loading', 'loading-images');
    }
  }
};

initializeUnitPriceFilterOnLoad();


class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);